import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import { FaSearch, FaUserCog, FaUsers, FaShieldAlt, FaCheckCircle } from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { collection, doc, getDoc, getDocs, getFirestore, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { logBusinessAction } from "../../services/AuditService";
import { useLocation } from "react-router-dom";

const db = getFirestore(firebaseApp);

const GRADIENT = "linear-gradient(135deg, #4c1d95 0%, #7c3aed 60%, #a78bfa 100%)";
const PURPLE700 = "#6d28d9";
const PURPLE100 = "#ede9fe";
const ACCENT = "#f59e0b";

const DEFAULT_ROLE_OPTIONS = [
  { code: "admin", label: "admin", order: 1 },
  { code: "rh", label: "RH", order: 2 },
  { code: "supervisor", label: "supervisor", order: 3 },
  { code: "analista", label: "analista", order: 4 },
  { code: "asistente", label: "asistente", order: 5 },
  { code: "seguridad", label: "seguridad", order: 6 },
  { code: "medico", label: "medico", order: 7 },
  { code: "almacenero", label: "almacenero", order: 8 },
  { code: "capacitador", label: "capacitador", order: 9 },
  { code: "ti", label: "TI", order: 10 },
  { code: "planner", label: "planner", order: 11 },
  { code: "admin_nomina", label: "nomina", order: 12 },
  { code: "chofer", label: "chofer", order: 13 },
  { code: "user", label: "user", order: 14 },
];
const NON_ASSIGNABLE_ROLES = new Set(["superAdmin"]);
const ensureUserRoleOption = (options = []) => {
  const hasUser = options.some((r) => String(r.code || "").trim().toLowerCase() === "user");
  if (hasUser) return options;
  return [...options, { code: "user", label: "user", order: 999 }];
};
const ensurePlannerRoleOption = (options = []) => {
  const hasPlanner = options.some((r) => String(r.code || "").trim().toLowerCase() === "planner");
  if (hasPlanner) return options;
  return [...options, { code: "planner", label: "planner", order: 997 }];
};
const ensurePayrollRoleOption = (options = []) => {
  const hasPayroll = options.some((r) => String(r.code || "").trim().toLowerCase() === "admin_nomina");
  if (hasPayroll) return options;
  return [...options, { code: "admin_nomina", label: "nomina", order: 998 }];
};
const ensureChoferRoleOption = (options = []) => {
  const hasChofer = options.some((r) => String(r.code || "").trim().toLowerCase() === "chofer");
  if (hasChofer) return options;
  return [...options, { code: "chofer", label: "chofer", order: 996 }];
};
const sortRoleOptionsAlpha = (options = []) =>
  [...options].sort((a, b) => String(a.label || a.code || "").localeCompare(String(b.label || b.code || ""), "es", { sensitivity: "base" }));

function PermisosRoles({ usuario }) {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState("");
  const [users, setUsers] = useState([]);
  const [roleOptions, setRoleOptions] = useState(DEFAULT_ROLE_OPTIONS);
  const [draftRoles, setDraftRoles] = useState({});
  const [search, setSearch] = useState("");
  const [searchApplied, setSearchApplied] = useState(false);

  const canManage = useMemo(() => ["admin", "superAdmin"].includes(String(usuario?.rol || "")), [usuario]);
  const isSuperAdmin = String(usuario?.rol || "") === "superAdmin";
  const tenantId = String(usuario?.ruc || "").trim();

  const getNormalizedRoleCode = (rawRole) => {
    const value = String(rawRole || "").trim();
    if (!value) return "";
    const lower = value.toLowerCase();
    if (lower === "administrador") return "admin";
    if (lower === "super administrador") return "superAdmin";
    if (lower === "recursos humanos") return "rh";
    if (lower === "it") return "ti";
    if (lower === "administrador de nomina" || lower === "administrador de nómina" || lower === "nomina") return "admin_nomina";
    return value;
  };

  const loadRolesCatalog = async () => {
    try {
      let snap = await getDocs(query(collection(db, `governance/${tenantId}/rolesCatalog`), orderBy("order", "asc")));
      if (snap.empty) {
        snap = await getDocs(query(collection(db, "rolesCatalog"), orderBy("order", "asc")));
      }
      if (snap.empty) {
        setRoleOptions(
          sortRoleOptionsAlpha(
            ensureUserRoleOption(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(DEFAULT_ROLE_OPTIONS))))
          )
        );
        return;
      }
      const options = snap.docs.map((d) => { const data = d.data() || {}; return { code: String(data.code || d.id || "").trim(), label: String(data.label || data.code || d.id || "").trim(), order: Number(data.order ?? 999) }; }).filter((r) => r.code);
      const filtered = (options.length ? options : DEFAULT_ROLE_OPTIONS).filter((r) => !NON_ASSIGNABLE_ROLES.has(String(r.code || "").trim()));
      setRoleOptions(
        sortRoleOptionsAlpha(
          ensureUserRoleOption(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(filtered))))
        )
      );
    } catch (e) {
      setRoleOptions(
        sortRoleOptionsAlpha(
          ensureUserRoleOption(ensureChoferRoleOption(ensurePlannerRoleOption(ensurePayrollRoleOption(DEFAULT_ROLE_OPTIONS))))
        )
      );
    }
  };

  const loadUsers = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("rucEmpresa", "==", tenantId)));
      const rows = snap.docs.map((d) => { const data = d.data() || {}; return { id: d.id, dni: String(data.dni || ""), name: String(data.name || ""), apellidos: String(data.apellidos || ""), email: String(data.email || ""), rol: String(data.rol || ""), activo: data.activo !== false }; });
      rows.sort((a, b) => `${a.apellidos} ${a.name}`.localeCompare(`${b.apellidos} ${b.name}`));
      setUsers(rows);
      const nd = {}; rows.forEach((u) => { nd[u.id] = getNormalizedRoleCode(u.rol) || ""; }); setDraftRoles(nd);
    } catch (e) { Swal.fire("Error", "No se pudo cargar la lista de usuarios.", "error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRolesCatalog(); loadUsers(); }, [tenantId]); // eslint-disable-line
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const dni = String(params.get("dni") || "").trim();
    if (!dni) return;
    setSearch(dni);
    setSearchApplied(true);
  }, [location.search]);

  const filteredUsers = useMemo(() => { const q = search.trim().toLowerCase(); if (!searchApplied || q.length < 2) return []; return users.filter((u) => `${u.name} ${u.apellidos} ${u.email} ${u.dni}`.toLowerCase().includes(q)).slice(0, 50); }, [users, search, searchApplied]);
  const stats = useMemo(
    () => ({
      total: users.length,
      activos: users.filter((u) => u.activo).length,
      conRol: users.filter((u) => {
        const role = getNormalizedRoleCode(u.rol).toLowerCase();
        return role && role !== "user";
      }).length
    }),
    [users]
  );

  const saveRole = async (row) => {
    if (!canManage) return;
    const nextRole = getNormalizedRoleCode(draftRoles[row.id]);
    if (!nextRole) { Swal.fire("Validación", "Debe seleccionar un rol válido.", "warning"); return; }
    if (nextRole === "admin" && !isSuperAdmin) {
      Swal.fire("Acceso restringido", "Solo un superAdmin puede asignar el rol admin.", "warning");
      return;
    }
    if (nextRole === getNormalizedRoleCode(row.rol)) return;
    try {
      setSavingId(row.id);
      const userRef = doc(db, "users", row.id);

      if (nextRole !== "user" && tenantId) {
        const templateRef = doc(db, `governance/${tenantId}/rolePermissions`, nextRole);
        const templateSnap = await getDoc(templateRef);
        const templatePerms = templateSnap.exists() ? (templateSnap.data()?.permisos || {}) : {};
        const hasActivePerms = Object.values(templatePerms).some(
          (v) => v === true || (Array.isArray(v) && v.length > 0)
        );
        if (!templateSnap.exists() || !hasActivePerms) {
          Swal.fire(
            "Plantilla requerida",
            `El rol "${nextRole}" no tiene una plantilla activa de permisos para esta empresa. Configúrala en "Permisos por Rol" antes de asignarlo.`,
            "warning"
          );
          setSavingId("");
          return;
        }
      }

      // 1) Limpiar permisos actuales y asignar rol nuevo
      await updateDoc(userRef, { rol: nextRole, permisos: {}, updated_at: serverTimestamp() });

      // 2) Si no es user, aplicar plantilla del nuevo rol para esta empresa
      let templateApplied = false;
      if (nextRole !== "user" && tenantId) {
        const roleTemplateRef = doc(db, `governance/${tenantId}/rolePermissions`, nextRole);
        const roleTemplateSnap = await getDoc(roleTemplateRef);
        const nextPerms = roleTemplateSnap.exists()
          ? (roleTemplateSnap.data()?.permisos || {})
          : {};
        templateApplied = roleTemplateSnap.exists();
        await updateDoc(userRef, { permisos: nextPerms, updated_at: serverTimestamp() });
      }

      try { await logBusinessAction("ASIGNACION_ROL", "RolesYPermisos", { dniEditado: row.dni, cambiosRealizados: { rol: { anterior: row.rol || "Sin rol", nuevo: nextRole } }, usuarioId: row.id }, usuario); } catch (e) { console.warn(e); }
      setUsers((prev) => prev.map((u) => (u.id === row.id ? { ...u, rol: nextRole } : u)));
      const syncText = nextRole === "user"
        ? "Permisos reiniciados a perfil base (user)."
        : (templateApplied
          ? "Plantilla del rol aplicada automáticamente."
          : "No se encontró plantilla para ese rol; quedó con permisos vacíos.");
      Swal.fire({ icon: "success", title: "Rol actualizado", text: `Se asignó "${nextRole}" a ${row.name} ${row.apellidos}. ${syncText}`, timer: 2200, showConfirmButton: false });
    } catch (e) { Swal.fire("Error", "No se pudo actualizar el rol.", "error"); }
    finally { setSavingId(""); }
  };

  const statCards = [
    { label: "Usuarios de la empresa", value: stats.total, bg: "linear-gradient(135deg,#ede9fe,#ddd6fe)", icon: <FaUsers color={PURPLE700} size={22} /> },
    { label: "Usuarios activos", value: stats.activos, bg: "linear-gradient(135deg,#ecfdf5,#d1fae5)", icon: <FaCheckCircle color="#059669" size={22} /> },
    { label: "Con rol asignado", value: stats.conRol, bg: "linear-gradient(135deg,#fff7ed,#fed7aa)", icon: <FaUserCog color="#d97706" size={22} /> },
  ];

  return (
    <div className="min-vh-100" style={{ background: "#f5f3ff", padding: "24px 0" }}>
      <style>{`
        .pr-stat:hover{transform:translateY(-3px);box-shadow:0 8px 24px rgba(109,40,217,.15)!important}
        .pr-row:hover{background:#f5f3ff!important}
        .pr-btn{transition:all .2s}
        .pr-btn:hover:not(:disabled){transform:scale(1.04);box-shadow:0 4px 12px rgba(109,40,217,.3)}
        .pr-input:focus{border-color:#7c3aed!important;box-shadow:0 0 0 3px rgba(124,58,237,.15)!important}
      `}</style>

      <Container fluid style={{ width: "94%", maxWidth: 1500 }}>
        <Card style={{ borderRadius: 15, overflow: "hidden", boxShadow: "0 8px 32px rgba(109,40,217,.1)", border: "none" }}>

          {/* Header */}
          <Card.Header className="text-white py-4 position-relative" style={{ background: GRADIENT, borderBottom: `3px solid ${ACCENT}`, borderRadius: "15px 15px 0 0" }}>
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center w-100">
              <h2 className="fw-bold mb-1">
                <FaUserCog className="me-3" size={26} />
                Asignar Roles
              </h2>
              <p className="mb-0 opacity-75">
                Busca un usuario por nombre o DNI y cambia su rol operativo
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ background: "#faf9ff" }}>
            {!canManage && (
              <Alert className="mb-4 d-flex align-items-center gap-2" style={{ borderRadius: 12, border: "1px solid #fbbf24", background: "#fffbeb", color: "#92400e" }}>
                <FaShieldAlt color="#d97706" /><span>Solo <strong>Admin</strong> y <strong>SuperAdmin</strong> pueden modificar roles.</span>
              </Alert>
            )}

            {/* Stat cards */}
            <Row className="g-3 mb-4">
              {statCards.map((s, i) => (
                <Col md={4} key={i}>
                  <Card className="pr-stat border-0" style={{ background: s.bg, borderRadius: 14, boxShadow: "0 2px 12px rgba(109,40,217,.08)", transition: "transform .2s,box-shadow .2s" }}>
                    <Card.Body className="d-flex align-items-center gap-3 py-3 px-4">
                      <div style={{ background: "rgba(255,255,255,.6)", borderRadius: 12, width: 46, height: 46, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{s.icon}</div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: ".8rem" }}>{s.label}</small>
                        <h3 className="mb-0 fw-bold" style={{ fontSize: "1.8rem", color: "#1e1b4b" }}>{s.value}</h3>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Búsqueda */}
            <Row className="mb-4">
              <Col md={9} lg={8}>
                <InputGroup>
                  <InputGroup.Text style={{ background: PURPLE100, border: "1.5px solid #ddd6fe", borderRight: "none", color: PURPLE700 }}><FaSearch /></InputGroup.Text>
                  <Form.Control className="pr-input" placeholder="Buscar por DNI o nombre (mínimo 2 caracteres)" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") setSearchApplied(true); }} style={{ border: "1.5px solid #ddd6fe", borderLeft: "none" }} />
                  <Button onClick={() => setSearchApplied(true)} style={{ background: GRADIENT, border: "none", fontWeight: 600, padding: "0 20px" }}>Buscar</Button>
                  <Button variant="outline-secondary" onClick={() => { setSearch(""); setSearchApplied(false); }} style={{ borderRadius: "0 8px 8px 0" }}>Limpiar</Button>
                </InputGroup>
              </Col>
              <Col className="d-flex align-items-center justify-content-end">
                {searchApplied && (
                  <span style={{ background: PURPLE100, color: PURPLE700, borderRadius: 50, padding: "5px 14px", fontWeight: 600, fontSize: ".85rem" }}>
                    <FaUsers className="me-1" />{filteredUsers.length} resultado(s)
                  </span>
                )}
              </Col>
            </Row>

            {/* Tabla */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: "#7c3aed", width: "3rem", height: "3rem" }} />
                <p className="mt-3 text-muted fw-semibold">Cargando usuarios...</p>
              </div>
            ) : (
              <div className="table-responsive" style={{ border: "1.5px solid #ede9fe", borderRadius: 14, boxShadow: "0 2px 12px rgba(109,40,217,.06)", overflow: "hidden" }}>
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr style={{ background: "linear-gradient(90deg,#ede9fe,#faf5ff)", borderBottom: "2px solid #ddd6fe" }}>
                      {["DNI", "Usuario", "Email", "Estado", "Rol", "Acción"].map((h, i) => (
                        <th key={h} className={i === 5 ? "text-end" : ""} style={{ color: PURPLE700, fontWeight: 700, fontSize: ".82rem", textTransform: "uppercase", letterSpacing: ".05em", padding: "14px 16px", whiteSpace: "nowrap", minWidth: i === 4 ? 260 : undefined }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => {
                      const cur = getNormalizedRoleCode(u.rol);
                      const sel = getNormalizedRoleCode(draftRoles[u.id] || "");
                      const hasChanges = sel !== cur;
                      const inCatalog = roleOptions.some((r) => r.code === sel);
                      return (
                        <tr key={u.id} className="pr-row" style={{ borderBottom: "1px solid #f3f0ff", transition: "background .15s" }}>
                          <td style={{ fontWeight: 700, color: "#4c1d95" }}>{u.dni || "–"}</td>
                          <td style={{ fontWeight: 600 }}>{`${u.apellidos} ${u.name}`.trim() || "–"}</td>
                          <td style={{ color: "#6b7280", fontSize: ".9rem" }}>{u.email || "–"}</td>
                          <td>
                            <span style={{ background: u.activo ? "linear-gradient(90deg,#059669,#34d399)" : "linear-gradient(90deg,#6b7280,#9ca3af)", color: "#fff", borderRadius: 20, padding: "4px 12px", fontWeight: 600, fontSize: ".78rem" }}>
                              {u.activo ? "Activo" : "Inactivo"}
                            </span>
                          </td>
                          <td>
                            <div className="mb-1"><small style={{ color: "#6b7280", fontSize: ".78rem" }}>Actual: <strong style={{ color: PURPLE700 }}>{u.rol || "Sin rol"}</strong></small></div>
                            <Form.Select size="sm" value={draftRoles[u.id] || ""} onChange={(e) => setDraftRoles((p) => ({ ...p, [u.id]: e.target.value }))} disabled={!canManage} style={{ borderRadius: 8, fontSize: ".88rem", borderColor: "#ddd6fe" }}>
                              <option value="">Seleccione rol</option>
                              {!inCatalog && sel && <option value={sel}>{sel} (actual)</option>}
                              {roleOptions.map((r) => (
                                <option
                                  key={r.code}
                                  value={r.code}
                                  disabled={
                                    (!isSuperAdmin && String(r.code || "").trim() === "admin")
                                  }
                                >
                                  {r.label}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              className="me-2"
                              variant="outline-secondary"
                              onClick={() => setDraftRoles((p) => ({ ...p, [u.id]: "user" }))}
                              disabled={!canManage || cur === "user"}
                              style={{ borderRadius: 8, fontWeight: 600, fontSize: ".82rem", padding: "6px 12px" }}
                            >
                              Quitar rol
                            </Button>
                            <Button size="sm" className="pr-btn" onClick={() => saveRole(u)} disabled={!canManage || !hasChanges || savingId === u.id}
                              style={{ background: hasChanges ? GRADIENT : "transparent", border: hasChanges ? "none" : "1.5px solid #d1d5db", color: hasChanges ? "#fff" : "#9ca3af", borderRadius: 8, fontWeight: 600, fontSize: ".82rem", padding: "6px 14px" }}>
                              {savingId === u.id ? <><Spinner animation="border" size="sm" className="me-1" />Guardando...</> : "Guardar rol"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-5">
                          <div style={{ background: PURPLE100, borderRadius: 50, width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}><FaSearch size={24} color={PURPLE700} /></div>
                          <p className="mb-1 fw-semibold" style={{ color: PURPLE700 }}>{!searchApplied ? "Ingrese DNI o nombre y presione Buscar" : search.trim().length < 2 ? "Escriba al menos 2 caracteres" : "No se encontraron usuarios con ese criterio"}</p>
                          {!searchApplied && <small className="text-muted">Use la barra de búsqueda para encontrar usuarios</small>}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default PermisosRoles;
